import {
  Activity,
  CircleCheck,
  CircleAlert,
  CircleX,
  Clock,
  Globe,
  Server,
  Inbox,
  Database,
  Sparkles,
  Mail,
  ArrowRight,
} from 'lucide-react';
import { LandingHeader } from '@/components/LandingHeader';
import { LandingFooter } from '@/components/LandingFooter';
import { LocalizedAnchor } from '@/components/LocalizedLink';
import { JsonLd } from '@/components/JsonLd';
import { useDocumentMeta } from '@/hooks/useDocumentMeta';
import { useT } from '@/i18n/provider';
import { SITE_ORIGIN, HREFLANG_TAGS, SITE_LOCALE } from '@/lib/site-config';
import { buttonVariants } from '@/components/ui/Button';

// ---------------------------------------------------------------------------
// Manual component status. Pre-Early Access we don't have automated monitoring
// driving this — it's a static page that we update when something is wrong.
// Once we wire up real probes (planned post-Early Access), this will be
// replaced by a live feed.
// ---------------------------------------------------------------------------

type Status = 'operational' | 'degraded' | 'outage' | 'maintenance';

const COMPONENT_STATUS: Record<string, Status> = {
  web: 'operational',
  api: 'operational',
  ingest: 'operational',
  query: 'operational',
  investigator: 'operational',
};

const COMPONENTS = [
  { id: 'web', icon: Globe },
  { id: 'api', icon: Server },
  { id: 'ingest', icon: Inbox },
  { id: 'query', icon: Database },
  { id: 'investigator', icon: Sparkles },
] as const;

const STATUS_VISUALS: Record<
  Status,
  { dot: string; chip: string; icon: React.ElementType }
> = {
  operational: {
    dot: 'bg-emerald-500',
    chip: 'text-emerald-300 bg-emerald-500/10 border-emerald-500/30',
    icon: CircleCheck,
  },
  degraded: {
    dot: 'bg-amber-500 animate-pulse',
    chip: 'text-amber-300 bg-amber-500/10 border-amber-500/30',
    icon: CircleAlert,
  },
  outage: {
    dot: 'bg-red-500 animate-pulse',
    chip: 'text-red-300 bg-red-500/10 border-red-500/30',
    icon: CircleX,
  },
  maintenance: {
    dot: 'bg-sky-500 animate-pulse',
    chip: 'text-sky-300 bg-sky-500/10 border-sky-500/30',
    icon: Clock,
  },
};

const TARGETS = [
  { key: 'uptime', value: '99.9%' },
  { key: 'queryLatency', value: '< 200ms' },
  { key: 'ingestLatency', value: '< 500ms' },
  { key: 'rpo', value: '< 1h' },
] as const;

function deriveOverallStatus(): Status {
  const statuses = Object.values(COMPONENT_STATUS);
  if (statuses.includes('outage')) return 'outage';
  if (statuses.includes('degraded')) return 'degraded';
  if (statuses.includes('maintenance')) return 'maintenance';
  return 'operational';
}

function ComponentRow({
  componentId,
  Icon,
  t,
}: {
  componentId: string;
  Icon: React.ElementType;
  t: (key: string) => string;
}) {
  const status = COMPONENT_STATUS[componentId] ?? 'operational';
  const visuals = STATUS_VISUALS[status];
  return (
    <div className="flex items-center gap-4 p-4 rounded-xl bg-gray-900/50 border border-gray-800 hover:border-primary-500/30 transition-colors">
      <div className="w-10 h-10 rounded-lg bg-primary-500/10 flex items-center justify-center flex-shrink-0">
        <Icon className="h-5 w-5 text-primary-400" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-white">
          {t(`status.components.${componentId}.label`)}
        </p>
        <p className="text-xs text-gray-500">
          {t(`status.components.${componentId}.description`)}
        </p>
      </div>
      <span
        className={`inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1 rounded-full border ${visuals.chip}`}
      >
        <span className={`h-1.5 w-1.5 rounded-full ${visuals.dot}`} />
        {t(`status.statusLabel.${status}`)}
      </span>
    </div>
  );
}

export function StatusPage() {
  const t = useT();

  useDocumentMeta({
    title: t('status.meta.title'),
    description: t('status.meta.description'),
  });

  const pageJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: t('status.meta.title'),
    description: t('status.meta.description'),
    url: `${SITE_ORIGIN}/status`,
    inLanguage: HREFLANG_TAGS[SITE_LOCALE],
    publisher: {
      '@type': 'Organization',
      name: 'LogPulse',
      url: SITE_ORIGIN,
    },
  };

  const overall = deriveOverallStatus();
  const overallVisuals = STATUS_VISUALS[overall];
  const OverallIcon = overallVisuals.icon;

  return (
    <div className="min-h-screen bg-gray-950">
      <JsonLd data={pageJsonLd} />
      <LandingHeader />

      {/* Hero — overall status */}
      <section className="pt-32 pb-12 sm:pt-40 sm:pb-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary-500/10 border border-primary-500/20 text-primary-400 text-sm font-medium mb-6">
            <Activity className="h-4 w-4" />
            {t('status.hero.badge')}
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">
            {t('status.hero.title')}
          </h1>
          <div
            className={`inline-flex items-center gap-2.5 px-5 py-2.5 rounded-full border ${overallVisuals.chip} text-sm font-semibold mb-4`}
          >
            <OverallIcon className="h-4 w-4" />
            {t(`status.overall.${overall}`)}
          </div>
          <p className="text-sm text-gray-500 max-w-2xl mx-auto leading-relaxed">
            {t('status.hero.disclaimer')}
          </p>
        </div>
      </section>

      {/* Components */}
      <section className="py-12 sm:py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-6">
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-2">
              {t('status.components.title')}
            </h2>
            <p className="text-gray-400">{t('status.components.subtitle')}</p>
          </div>
          <div className="space-y-3">
            {COMPONENTS.map(({ id, icon }) => (
              <ComponentRow key={id} componentId={id} Icon={icon} t={t} />
            ))}
          </div>
        </div>
      </section>

      {/* Reliability targets */}
      <section className="py-12 sm:py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-6">
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-2">
              {t('status.targets.title')}
            </h2>
            <p className="text-gray-400">{t('status.targets.subtitle')}</p>
          </div>
          <div className="rounded-2xl bg-gray-900/50 border border-gray-800 overflow-hidden">
            <div className="grid grid-cols-1 sm:grid-cols-2 divide-x-0 sm:divide-x divide-y sm:divide-y-0 divide-gray-800 sm:[&>*:nth-child(2)]:border-t-0 sm:[&>*:nth-child(n+3)]:border-t sm:[&>*:nth-child(n+3)]:border-gray-800">
              {TARGETS.map((target) => (
                <div key={target.key} className="p-5 flex flex-col gap-1">
                  <p className="text-xs uppercase tracking-wide text-gray-500 font-medium">
                    {t(`status.targets.${target.key}.label`)}
                  </p>
                  <p className="text-2xl font-bold text-white tabular-nums">
                    {target.value}
                  </p>
                  <p className="text-xs text-gray-500">
                    {t(`status.targets.${target.key}.detail`)}
                  </p>
                </div>
              ))}
            </div>
          </div>
          <p className="text-sm text-gray-500 mt-4">
            {t('status.targets.footer')}{' '}
            <LocalizedAnchor
              href="/security"
              className="text-primary-400 hover:text-primary-300 transition-colors inline-flex items-center gap-1"
            >
              {t('status.targets.footerLink')}
              <ArrowRight className="h-3 w-3" />
            </LocalizedAnchor>
          </p>
        </div>
      </section>

      {/* Maintenance windows */}
      <section className="py-12 sm:py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="rounded-2xl bg-gray-900/50 border border-gray-800 p-6 sm:p-8">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-xl bg-sky-500/10 flex items-center justify-center flex-shrink-0">
                <Clock className="h-6 w-6 text-sky-400" />
              </div>
              <div className="flex-1">
                <h2 className="text-xl sm:text-2xl font-bold text-white mb-2">
                  {t('status.maintenance.title')}
                </h2>
                <p className="text-gray-400 mb-4 leading-relaxed">
                  {t('status.maintenance.body')}
                </p>
                <ul className="space-y-2">
                  {['notice', 'window', 'paid'].map((k) => (
                    <li
                      key={k}
                      className="flex items-start gap-2.5 text-sm text-gray-300 leading-relaxed"
                    >
                      <span className="text-primary-400 mt-1.5">•</span>
                      <span>{t(`status.maintenance.item.${k}`)}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Incident history — empty state */}
      <section className="py-12 sm:py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-6">
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-2">
              {t('status.incidents.title')}
            </h2>
            <p className="text-gray-400">{t('status.incidents.subtitle')}</p>
          </div>
          <div className="rounded-2xl border border-dashed border-gray-700 bg-gray-900/30 p-8 text-center">
            <div className="w-12 h-12 rounded-2xl bg-emerald-500/10 flex items-center justify-center mx-auto mb-4">
              <CircleCheck className="h-6 w-6 text-emerald-400" />
            </div>
            <p className="text-sm font-semibold text-gray-200 mb-1">
              {t('status.incidents.empty.title')}
            </p>
            <p className="text-sm text-gray-500 max-w-md mx-auto leading-relaxed">
              {t('status.incidents.empty.body')}
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 sm:py-24">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="rounded-3xl bg-gradient-to-br from-primary-500/10 to-primary-500/5 border border-primary-500/20 p-8 sm:p-12">
            <div className="w-14 h-14 rounded-2xl bg-primary-500/10 flex items-center justify-center mx-auto mb-5">
              <Activity className="h-7 w-7 text-primary-400" />
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">
              {t('status.cta.title')}
            </h2>
            <p className="text-gray-400 mb-8 leading-relaxed">
              {t('status.cta.body')}
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <a
                href="mailto:status@logpulse.io?subject=Status%20updates%20opt-in"
                className={buttonVariants({ variant: 'gradient', size: 'lg' })}
              >
                <Mail className="h-4 w-4 mr-2" />
                {t('status.cta.button')}
              </a>
              <LocalizedAnchor
                href="/security"
                className="inline-flex items-center gap-1.5 text-sm font-medium text-primary-400 hover:text-primary-300 transition-colors"
              >
                {t('status.cta.securityLink')}
                <ArrowRight className="h-3.5 w-3.5" />
              </LocalizedAnchor>
            </div>
          </div>
        </div>
      </section>

      <LandingFooter />
    </div>
  );
}
